import { useState, useEffect } from 'react';
import type { Account } from '../../../hooks/useAccounts';
import { CurrencyInput } from '../../../components/base/CurrencyInput';

interface TransferModalProps {
  open: boolean;
  accounts: Account[];
  onClose: () => void;
  onSave: (payload: {
    from_account_id: string;
    to_account_id: string;
    amount: number;
    date: string;
    notes?: string;
    status: 'pago' | 'pendente';
  }) => Promise<{ success: boolean; message: string }>;
}

const fmt = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function TransferModal({ open, accounts, onClose, onSave }: TransferModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const active = accounts.filter(a => !a.is_archived);

  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'pago' | 'pendente'>('pago');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFromId(active[0]?.id ?? '');
      setToId(active[1]?.id ?? '');
      setAmount(0);
      setDate(today);
      setNotes('');
      setStatus('pago');
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const fromAccount = active.find(a => a.id === fromId);
  const toAccount = active.find(a => a.id === toId);
  const lowBalance = fromAccount && amount > 0 && fromAccount.current_balance < amount;

  const handleSwap = () => { setFromId(toId); setToId(fromId); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fromId || !toId) { setError('Selecione as contas de origem e destino.'); return; }
    if (fromId === toId) { setError('Origem e destino não podem ser iguais.'); return; }
    if (!amount || amount <= 0) { setError('Informe um valor válido.'); return; }
    if (!date) { setError('Informe a data.'); return; }
    setLoading(true);
    const result = await onSave({ from_account_id: fromId, to_account_id: toId, amount, date, notes, status });
    setLoading(false);
    if (!result.success) setError(result.message);
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-teal-50 rounded-xl flex items-center justify-center">
              <i className="ri-arrow-left-right-line text-teal-500" />
            </div>
            <div>
              <h2 className="text-sm font-700 text-gray-900">Transferência entre Contas</h2>
              <p className="text-xs text-gray-400">Mova valores entre suas contas</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3">

          {/* Origem */}
          <div>
            <label className="text-[11px] font-600 text-gray-400 uppercase tracking-wide block mb-1">Origem</label>
            <div className="relative">
              <select value={fromId} onChange={e => setFromId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 cursor-pointer appearance-none">
                <option value="">Selecionar conta</option>
                {active.map(a => <option key={a.id} value={a.id}>{a.name} — {fmt(a.current_balance)}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
            </div>
            {fromAccount && (
              <p className="text-[11px] text-gray-400 mt-1 pl-0.5 flex items-center gap-1">
                <i className="ri-wallet-line text-[10px]" />
                Saldo: <span className={`font-600 ${fromAccount.current_balance >= 0 ? 'text-gray-600' : 'text-red-500'}`}>{fmt(fromAccount.current_balance)}</span>
                {lowBalance && <span className="text-amber-500 font-600 ml-1">· Saldo insuficiente</span>}
              </p>
            )}
          </div>

          {/* Swap + seta */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-100" />
            <button type="button" onClick={handleSwap}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-xs text-gray-500 hover:border-teal-300 hover:text-teal-600 cursor-pointer transition-all whitespace-nowrap">
              <i className="ri-arrow-up-down-line text-xs" />Inverter
            </button>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* Destino */}
          <div>
            <label className="text-[11px] font-600 text-gray-400 uppercase tracking-wide block mb-1">Destino</label>
            <div className="relative">
              <select value={toId} onChange={e => setToId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 cursor-pointer appearance-none">
                <option value="">Selecionar conta</option>
                {active.map(a => (
                  <option key={a.id} value={a.id} disabled={a.id === fromId}>
                    {a.name} — {fmt(a.current_balance)}{a.id === fromId ? ' (origem)' : ''}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
            </div>
          </div>

          {/* Preview compacto */}
          {fromAccount && toAccount && fromId !== toId && (
            <div className="flex items-center gap-2 bg-teal-50 rounded-xl px-3 py-2 border border-teal-100">
              <p className="flex-1 text-xs font-600 text-gray-700 truncate text-right">{fromAccount.name}</p>
              <i className="ri-arrow-right-line text-teal-400 text-sm shrink-0" />
              <p className="flex-1 text-xs font-600 text-gray-700 truncate">{toAccount.name}</p>
            </div>
          )}

          {/* Valor + Data em linha */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-600 text-gray-400 uppercase tracking-wide block mb-1">Valor</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-600 text-gray-400 z-10 pointer-events-none">R$</span>
                <CurrencyInput
                  value={amount}
                  onChange={(v) => setAmount(v)}
                  placeholder="0,00"
                  className="w-full pl-8 pr-2 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-teal-400"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-600 text-gray-400 uppercase tracking-wide block mb-1">Data</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-teal-400" />
            </div>
          </div>

          {/* Status + Observação em linha */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-600 text-gray-400 uppercase tracking-wide block mb-1">Status</label>
              <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
                {(['pago', 'pendente'] as const).map(s => (
                  <button key={s} type="button" onClick={() => setStatus(s)}
                    className={`flex-1 py-1 rounded-lg text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${status === s ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}>
                    {s === 'pago' ? 'Feita' : 'Agendada'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-600 text-gray-400 uppercase tracking-wide block mb-1">Observação</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Opcional..." maxLength={200}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-teal-400 placeholder:text-gray-300" />
            </div>
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
            <i className="ri-information-line text-gray-400 text-xs mt-0.5 shrink-0" />
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Gera 2 lançamentos vinculados. <strong className="text-gray-500">Não impacta receita, despesa ou patrimônio.</strong>
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 px-3 py-2 rounded-xl border border-red-100">
              <i className="ri-error-warning-line text-sm shrink-0" />{error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 py-2 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" disabled={loading || !fromId || !toId || fromId === toId || amount <= 0}
              className="flex-1 py-2 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-600 rounded-xl text-sm cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
              {loading && <i className="ri-loader-4-line animate-spin text-sm" />}
              {loading ? 'Transferindo...' : 'Confirmar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
